import { observer } from 'mobx-react-lite'
import React, { useState, useEffect, useRef } from 'react'

import chatbotStore, { Message } from '../../../stores/chatbotStore'
import type {
  IntentResult,
  QuickRepliesResponseTemplate,
  ResponseTemplate,
} from '../types/intent'

import { processUserMessage } from './IntentProcessor'
import { QuickActions } from './QuickActions'
import { ResponseRenderer } from './ResponseRenderer'

interface WidgetProps {
  onSendMessage?: (message: string) => void
  onIntentDetected?: (result: IntentResult) => void
  className?: string
  onClose?: () => void
}

export const ChatWidget: React.FC<WidgetProps> = observer(
  ({ onSendMessage, onIntentDetected, onClose, className = '' }) => {
    const [inputValue, setInputValue] = useState('')
    const messagesEndRef = useRef<HTMLDivElement>(null)

    // Use MobX store for messages and loading state
    const { messages, isLoading, addMessage, setLoading } = chatbotStore

    // Auto scroll to bottom when new messages arrive
    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
      scrollToBottom()
    }, [messages])

    // Initialize with welcome message
    useEffect(() => {
      // Welcome message is now handled in MobX store initialization
    }, [])

    // Handle quick actions
    const handleQuickAction = async (actionType: string) => {
      let quickMessage = ''

      switch (actionType) {
        case 'check-specialist':
          quickMessage = 'Check specialist availability'
          break
        case 'contact-support':
          quickMessage = 'I want to contact support team'
          break
        case 'view-services':
          quickMessage = 'View Kawaconn services list'
          break
        default:
          quickMessage = 'General support'
      }

      if (!quickMessage.trim()) {
        return
      }

      // Create user message immediately
      const userMessage: Message = {
        id: Date.now().toString(),
        text: quickMessage,
        sender: 'user',
        timestamp: new Date(),
        type: 'text',
      }

      // Add user message to chat
      addMessage(userMessage)
      setLoading(true)

      try {
        // Call callback if exists
        if (onSendMessage) {
          onSendMessage(quickMessage)
        }

        // Process message with IntentProcessor
        await processMessageWithIntentProcessor(quickMessage)
      } catch (error) {
        console.error('Error processing quick action:', error)
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Sorry, an error occurred. Please try again later.',
          sender: 'bot',
          timestamp: new Date(),
          type: 'text',
        }
        addMessage(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    const handleSendMessage = async () => {
      if (!inputValue.trim()) {
        return
      }

      const userMessage: Message = {
        id: Date.now().toString(),
        text: inputValue,
        sender: 'user',
        timestamp: new Date(),
        type: 'text',
      }

      addMessage(userMessage)
      setInputValue('')
      setLoading(true)

      try {
        // Call callback if exists
        if (onSendMessage) {
          onSendMessage(inputValue)
        }

        // Use enhanced IntentProcessor to process
        await processMessageWithIntentProcessor(inputValue)
      } catch (error) {
        console.error('Error sending message:', error)
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Sorry, an error occurred. Please try again later.',
          sender: 'bot',
          timestamp: new Date(),
          type: 'text',
        }
        addMessage(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    const processMessageWithIntentProcessor = async (userMessage: string) => {
      try {
        // Use IntentProcessor to process
        const intentResult = await processUserMessage(userMessage, {
          conversationId: `conv_${Date.now()}`,
          sessionData: { widget: 'botpress' },
        })

        // Call intent detection callback
        if (onIntentDetected) {
          onIntentDetected(intentResult)
        }

        // Create bot response from intent result
        const botMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: intentResult.response,
          sender: 'bot',
          timestamp: new Date(),
          type: 'text',
          intentData: intentResult,
          template: intentResult.template, // Add template from intent result
        }

        addMessage(botMessage)

        // If there are quick replies in data, add a separate message for quick replies
        if (
          intentResult.data?.quick_replies &&
          intentResult.template?.type === 'carousel'
        ) {
          const quickRepliesMessage: Message = {
            id: (Date.now() + 2).toString(),
            text: 'Additional options:', // Provide a fallback text
            sender: 'bot',
            timestamp: new Date(),
            type: 'text',
            template: {
              type: 'quick_replies',
              content: intentResult.data.quick_replies,
            } as QuickRepliesResponseTemplate,
          }

          setTimeout(() => {
            addMessage(quickRepliesMessage)
          }, 500) // Small delay for more natural effect
        }

        // Handle actions if any
        if (intentResult.actions && intentResult.actions.length > 0) {
          await handleIntentActions(intentResult.actions)
        }
      } catch (error) {
        console.error('Error processing with IntentProcessor:', error)

        // Fallback response
        const fallbackMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Thank you for contacting us! We will support you as soon as possible.',
          sender: 'bot',
          timestamp: new Date(),
          type: 'text',
        }
        addMessage(fallbackMessage)
      }
    }

    // Handle actions from intent
    const handleIntentActions = async (actions: any[]) => {
      for (const action of actions) {
        switch (action.type) {
          case 'callback':
            console.log('Intent callback:', action.payload)
            break

          case 'form':
            // Can show a form to collect information
            const formMessage: Message = {
              id: (Date.now() + Math.random()).toString(),
              text: '📝 Please provide more information so I can assist you better.',
              sender: 'bot',
              timestamp: new Date(),
              type: 'text',
            }
            addMessage(formMessage)
            break

          case 'redirect':
            // Can redirect to another page
            console.log('Redirect to:', action.payload.url)
            break

          default:
            console.log('Unknown action type:', action.type)
        }
      }
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSendMessage()
      }
    }

    return (
      <div className={`fixed bottom-24 right-6 z-[101] ${className}`}>
        {/* Chat Widget Toggle Button */}
        {/* {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg transition-all duration-300 hover:scale-110"
          aria-label="Open chat"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </button>
      )} */}

        {/* Chat Widget */}
        <div className="bg-white rounded-lg shadow-2xl w-[400px] h-[calc(100vh-150px)] max-h-[820px] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-blue-600 text-white py-1 px-4 flex justify-between items-center">
            <h3 className="font-semibold pt-2">Booking Assistant</h3>
            <button
              onClick={() => {
                onClose?.()
              }}
              className="text-white hover:text-grey-200 transition-colors"
              aria-label="Close chat"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map(message => (
              <div
                key={message.id}
                className={`flex ${
                  message.sender === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[80%] p-2 rounded-lg text-left ${
                    message.sender === 'user'
                      ? 'bg-blue-600 text-white rounded-br-none'
                      : 'bg-grey-100 text-grey-800 rounded-bl-none'
                  }`}
                >
                  {message.text && (
                    <div className="text-sm whitespace-pre-line">
                      {message.text}
                    </div>
                  )}
                  {/* Render template if exists, otherwise render plain text */}
                  {message.template && message.sender === 'bot' && (
                    <div>
                      <ResponseRenderer
                        template={message.template}
                        onAction={action => {
                          // Handle template actions (e.g., button clicks, form submissions)
                          if (action.type === 'postback' && action.value) {
                            setInputValue(action.value)
                            // Auto send message from value
                            setTimeout(() => handleSendMessage(), 100)
                          }

                          if (action.handleClick) {
                            action.handleClick()
                          }
                        }}
                        onQuickReply={payload => {
                          // Handle quick reply - auto send message
                          setInputValue(payload)
                          setTimeout(() => handleSendMessage(), 100)
                        }}
                        onFormSubmit={data => {
                          // Handle form submission
                          const formText = Object.entries(data)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join(', ')
                          setInputValue(`Form info: ${formText}`)
                          setTimeout(() => handleSendMessage(), 100)
                        }}
                      />
                    </div>
                  )}

                  <span className="text-xs opacity-70 mt-1 block text-right">
                    {message.timestamp.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-grey-200 text-grey-500 rounded-lg rounded-bl-none p-3">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-grey-400 rounded-full animate-bounce"></div>
                    <div
                      className="w-2 h-2 bg-grey-400 rounded-full animate-bounce"
                      style={{ animationDelay: '0.1s' }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-grey-400 rounded-full animate-bounce"
                      style={{ animationDelay: '0.2s' }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t p-2 border-grey-300">
            <div className="flex space-x-2">
              {/* Quick Actions Button */}
              <QuickActions onActionClick={handleQuickAction} />

              <textarea
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                className="flex-1 border border-grey-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={1}
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-grey-300 text-white rounded-lg px-4 py-0 text-sm font-medium transition-colors"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }
)
